import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Calculator, ChevronDown, ChevronRight, Info } from 'lucide-react';
import type { DepreciationItem } from '@/types/depreciation';
import { formatCurrency } from '@/lib/format-currency';

interface BatchDetailGridProps {
  items: DepreciationItem[];
  onUpdateItem: (itemId: string, updates: Partial<DepreciationItem>) => void;
  readonly?: boolean;
  selectedItemIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
}

export function BatchDetailGrid({ items, onUpdateItem, readonly = false, selectedItemIds = new Set(), onSelectionChange }: BatchDetailGridProps): React.ReactElement {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<DepreciationItem | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [focusedCell, setFocusedCell] = useState<string | null>(null);

  const toggleRow = (itemId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange(new Set(items.map(item => item.id)));
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (!onSelectionChange) return;
    const newSelection = new Set(selectedItemIds);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    onSelectionChange(newSelection);
  };

  const handlePercentFocus = (itemId: string, currentValue: number) => {
    const cellKey = `${itemId}-percent`;
    setFocusedCell(cellKey);
    setEditingValues(prev => ({ ...prev, [cellKey]: currentValue.toFixed(2) }));
  };

  const handlePercentChange = (itemId: string, newPercent: string) => {
    const cellKey = `${itemId}-percent`;
    // Allow empty string, numbers, and one decimal point
    if (newPercent === '' || /^\d*\.?\d*$/.test(newPercent)) {
      setEditingValues(prev => ({ ...prev, [cellKey]: newPercent }));
    }
  };

  const handlePercentBlur = (item: DepreciationItem) => {
    const cellKey = `${item.id}-percent`;
    setFocusedCell(null);
    const newPercent = editingValues[cellKey];
    
    if (!newPercent || newPercent === '') {
      setEditingValues(prev => {
        const updated = { ...prev };
        delete updated[cellKey];
        return updated;
      });
      return;
    }

    const percent = parseFloat(newPercent);
    if (isNaN(percent)) {
      setEditingValues(prev => {
        const updated = { ...prev };
        delete updated[cellKey];
        return updated;
      });
      return;
    }

    const newValue = (item.openingNBV * percent) / 100;
    const newClosingNBV = Math.max(item.openingNBV - newValue, item.residualValue);

    onUpdateItem(item.id, {
      depreciationPercent: percent,
      depreciationValue: newValue,
      closingNBV: newClosingNBV,
      isManualOverride: false,
    });

    setEditingValues(prev => {
      const updated = { ...prev };
      delete updated[cellKey];
      return updated;
    });
  };

  const handleValueFocus = (itemId: string, currentValue: number) => {
    const cellKey = `${itemId}-value`;
    setFocusedCell(cellKey);
    setEditingValues(prev => ({ ...prev, [cellKey]: currentValue.toFixed(2) }));
  };

  const handleValueChange = (itemId: string, newValue: string) => {
    const cellKey = `${itemId}-value`;
    // Allow empty string, numbers, and one decimal point
    if (newValue === '' || /^\d*\.?\d*$/.test(newValue)) {
      setEditingValues(prev => ({ ...prev, [cellKey]: newValue }));
    }
  };

  const handleValueBlur = (item: DepreciationItem) => {
    const cellKey = `${item.id}-value`;
    setFocusedCell(null);
    const newValue = editingValues[cellKey];
    
    if (!newValue || newValue === '') {
      setEditingValues(prev => {
        const updated = { ...prev };
        delete updated[cellKey];
        return updated;
      });
      return;
    }

    const value = parseFloat(newValue);
    if (isNaN(value)) {
      setEditingValues(prev => {
        const updated = { ...prev };
        delete updated[cellKey];
        return updated;
      });
      return;
    }

    const newPercent = (value / item.openingNBV) * 100;
    const newClosingNBV = Math.max(item.openingNBV - value, item.residualValue);

    onUpdateItem(item.id, {
      depreciationPercent: newPercent,
      depreciationValue: value,
      closingNBV: newClosingNBV,
      isManualOverride: false,
    });

    setEditingValues(prev => {
      const updated = { ...prev };
      delete updated[cellKey];
      return updated;
    });
  };

  const confirmOverride = () => {
    if (!currentItem || !overrideReason.trim()) return;

    const item = currentItem as any;
    onUpdateItem(currentItem.id, {
      depreciationPercent: item._pendingPercent,
      depreciationValue: item._pendingValue,
      closingNBV: item._pendingClosingNBV,
      isManualOverride: true,
      overrideReason: overrideReason,
      overrideBy: 'Current User',
      overrideDate: new Date().toISOString(),
    });

    setOverrideDialogOpen(false);
    setOverrideReason('');
    setCurrentItem(null);
  };

  return (
    <>
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
            <TableRow className="border-t-2 border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
              {!readonly && onSelectionChange && (
                <TableHead className="h-12 font-semibold w-[40px] pl-6">
                  <Checkbox
                    checked={selectedItemIds.size === items.length && items.length > 0}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </TableHead>
              )}
              {!readonly && <TableHead className="h-12 font-semibold w-[40px]"></TableHead>}
              <TableHead className="h-12 font-semibold w-[120px]">Asset Code</TableHead>
              <TableHead className="h-12 font-semibold min-w-[200px]">Asset Description</TableHead>
              <TableHead className="h-12 font-semibold text-right w-[140px]">Acquisition Cost (£)</TableHead>
              <TableHead className="h-12 font-semibold text-right w-[140px]">Opening NBV (£)</TableHead>
              <TableHead className="h-12 font-semibold text-right w-[120px]">Residual Value (£)</TableHead>
              <TableHead className="h-12 font-semibold w-[120px]">Method</TableHead>
              <TableHead className="h-12 font-semibold text-right w-[130px]">Life Remaining</TableHead>
              <TableHead className="h-12 font-semibold text-right w-[140px]">Depreciation %</TableHead>
              <TableHead className="h-12 font-semibold text-right w-[180px]">Depreciation Value (£)</TableHead>
              <TableHead className="h-12 font-semibold text-right w-[140px] pr-6">Closing NBV (£)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, index) => (
                <>
                  <TableRow 
                    key={item.id}
                    className={`
                      ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                      hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                    `}
                  >
                    {!readonly && onSelectionChange && (
                      <TableCell className="w-[40px] pl-6">
                        <Checkbox
                          checked={selectedItemIds.has(item.id)}
                          onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                          aria-label={`Select ${item.assetCode}`}
                        />
                      </TableCell>
                    )}
                    {!readonly && (
                      <TableCell className="w-[40px]">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleRow(item.id)}
                          className="h-6 w-6 p-0"
                        >
                          {expandedRows.has(item.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    )}
                    <TableCell className="font-medium w-[120px] text-slate-700 dark:text-slate-300">{item.assetCode}</TableCell>
                    <TableCell className="min-w-[200px] text-slate-700 dark:text-slate-300">
                      {item.assetDescription}
                    </TableCell>
                    <TableCell className="text-right w-[140px] text-slate-700 dark:text-slate-300">{formatCurrency(item.acquisitionCost)}</TableCell>
                    <TableCell className="text-right w-[140px] text-slate-700 dark:text-slate-300">{formatCurrency(item.openingNBV)}</TableCell>
                    <TableCell className="text-right w-[120px] text-slate-700 dark:text-slate-300">{formatCurrency(item.residualValue)}</TableCell>
                    <TableCell className="w-[120px] text-slate-700 dark:text-slate-300">{item.method}</TableCell>
                    <TableCell className="text-right w-[130px] text-slate-700 dark:text-slate-300">{item.lifeRemaining.toFixed(2)} yrs</TableCell>
                    <TableCell className="w-[140px]">
                      {readonly ? (
                        <div className="text-right text-slate-700 dark:text-slate-300">{formatCurrency(item.depreciationPercent ?? 0)}%</div>
                      ) : (
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={focusedCell === `${item.id}-percent` ? (editingValues[`${item.id}-percent`] ?? item.depreciationPercent?.toFixed(2) ?? '') : (item.depreciationPercent?.toFixed(2) ?? '')}
                          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handlePercentChange(item.id, e.target.value)}
                          onFocus={() => handlePercentFocus(item.id, item.depreciationPercent ?? 0)}
                          onBlur={() => handlePercentBlur(item)}
                          className="text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      )}
                    </TableCell>
                    <TableCell className="w-[180px]">
                      {readonly ? (
                        <div className="text-right text-slate-700 dark:text-slate-300">{formatCurrency(item.depreciationValue)}</div>
                      ) : (
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={focusedCell === `${item.id}-value` ? (editingValues[`${item.id}-value`] ?? item.depreciationValue.toFixed(2)) : formatCurrency(item.depreciationValue)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange(item.id, e.target.value)}
                          onFocus={() => handleValueFocus(item.id, item.depreciationValue)}
                          onBlur={() => handleValueBlur(item)}
                          className="text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium w-[140px] text-slate-700 dark:text-slate-300 pr-6">{formatCurrency(item.closingNBV)}</TableCell>
                  </TableRow>

                  {/* Expanded Row - Explainability */}
                  {!readonly && expandedRows.has(item.id) && (
                    <TableRow key={`${item.id}-expanded`} className="bg-muted/50">
                      <TableCell colSpan={13} className="bg-muted/50">
                        <div className="p-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left side - Calculation Inputs */}
                            <div className="space-y-3">
                              <h4 className="font-semibold flex items-center gap-2">
                                <Info className="h-4 w-4" />
                                Why this value?
                              </h4>
                              {item.calculationInputs && (
                                <div className="space-y-2 text-sm">
                                  {/* User Edit Warning */}
                                  {item.calculationInputs.isUserEdited && (
                                    <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                                      <strong>Note:</strong> This value has been manually edited.
                                      {item.calculationInputs.userEditedNote && (
                                        <span className="block mt-1">{item.calculationInputs.userEditedNote}</span>
                                      )}
                                    </div>
                                  )}
                                
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="text-muted-foreground">Method:</div>
                                    <div>{item.calculationInputs.method || item.calculationInputs.projectMethod}</div>
                                    <div className="text-muted-foreground">Residual Value:</div>
                                    <div>£{formatCurrency(item.calculationInputs.residualValue ?? 0)}</div>
                                    {item.calculationInputs.inServiceDate && (
                                      <>
                                        <div className="text-muted-foreground">Acquisition Date:</div>
                                        <div>{new Date(item.calculationInputs.inServiceDate).toLocaleDateString()}</div>
                                      </>
                                    )}
                                  
                                    {/* Straight Line / Period Ratio specific fields */}
                                    {(item.calculationInputs.method === 'Straight Line' || item.calculationInputs.method === 'Period Ratio') && (
                                      <>
                                        <div className="text-muted-foreground">Useful Life:</div>
                                        <div>{item.calculationInputs.usefulLife} years</div>
                                        <div className="text-muted-foreground">Posting Frequency:</div>
                                        <div>{item.calculationInputs.postingFrequency ?? 'Monthly'}</div>
                                        <div className="text-muted-foreground">Base Value On:</div>
                                        <div>{item.calculationInputs.baseValueOn ?? 'Original Postings'}</div>
                                        {item.calculationInputs.currentNBV !== undefined && item.calculationInputs.currentNBV !== null && (
                                          <>
                                            <div className="text-muted-foreground">Current NBV:</div>
                                            <div>£{formatCurrency(item.calculationInputs.currentNBV ?? 0)}</div>
                                          </>
                                        )}
                                        <div className="text-muted-foreground">Total Postings:</div>
                                        <div>{item.calculationInputs.totalPostings}</div>
                                        <div className="text-muted-foreground">Postings Made:</div>
                                        <div>{item.calculationInputs.postingsMade}</div>
                                      </>
                                    )}
                                  
                                    {/* Reducing Balance specific fields */}
                                    {item.calculationInputs.method === 'Reducing Balance' && (
                                      <>
                                        <div className="text-muted-foreground">Depreciation %:</div>
                                        <div>{item.calculationInputs.depreciationPercentage?.toFixed(2)}%</div>
                                        <div className="text-muted-foreground">Posting Frequency:</div>
                                        <div>{item.calculationInputs.postingFrequency ?? 'Monthly'}</div>
                                        <div className="text-muted-foreground">Start of Year NBV:</div>
                                        <div>£{formatCurrency(item.calculationInputs.startOfYearNBV ?? 0)}</div>
                                      </>
                                    )}
                                  
                                    {/* Sum of Digits specific fields */}
                                    {item.calculationInputs.method === 'Sum of Digits' && (
                                      <>
                                        <div className="text-muted-foreground">Depreciation %:</div>
                                        <div>N/A</div>
                                        <div className="text-muted-foreground">Posting Frequency:</div>
                                        <div>N/A</div>
                                        <div className="text-muted-foreground">Total Postings:</div>
                                        <div>{item.calculationInputs.totalPostings}</div>
                                        <div className="text-muted-foreground">Postings Made:</div>
                                        <div>{item.calculationInputs.postingsMade}</div>
                                        <div className="text-muted-foreground">Sum of Digits:</div>
                                        <div>{item.calculationInputs.sumOfDigits}</div>
                                        <div className="text-muted-foreground">Remaining Postings:</div>
                                        <div>{item.calculationInputs.remainingPostings}</div>
                                      </>
                                    )}
                                  
                                    {/* Period Ratio specific fields */}
                                    {item.calculationInputs.method === 'Period Ratio' && item.calculationInputs.periodWeeks && (
                                      <>
                                        <div className="text-muted-foreground">Period Weeks:</div>
                                        <div>{item.calculationInputs.periodWeeks}</div>
                                      </>
                                    )}
                                  
                                    {/* Current Base Value */}
                                    {item.calculationInputs.currentBaseValue !== undefined && (
                                      <>
                                        <div className="text-muted-foreground">Current Base Value:</div>
                                        <div>£{formatCurrency(item.calculationInputs.currentBaseValue ?? 0)}</div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Right side - Formula Section */}
                            <div className="space-y-3">
                              <h4 className="font-semibold flex items-center gap-2">
                                <Calculator className="h-4 w-4" />
                                Formula
                              </h4>
                              {item.calculationInputs && (
                                <div className="space-y-2 text-sm">
                                  <div className="pt-2 border-t">
                                    <div className="text-muted-foreground mb-1">Formula:</div>
                                    <code className="text-xs bg-muted px-2 py-1 rounded block">
                                      {item.calculationInputs.formula}
                                    </code>
                                  </div>
                                
                                  {item.calculationInputs.formulaBreakdown && (
                                    <div className="pt-2">
                                      <div className="text-muted-foreground mb-1">Calculation Breakdown:</div>
                                      <pre className="text-xs bg-muted px-2 py-1 rounded whitespace-pre-wrap font-mono">
                                        {item.calculationInputs.formulaBreakdown}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
      </div>

      {/* Override Reason Dialog */}
      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Override Reason</DialogTitle>
            <DialogDescription>
              Manual override will replace the AI suggestion. Provide a reason for audit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for Override *</Label>
              <Textarea
                id="reason"
                value={overrideReason}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setOverrideReason(e.target.value)}
                placeholder="Explain why you're overriding the AI suggestion..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmOverride} disabled={!overrideReason.trim()}>
              Confirm Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
